import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  LinearProgress,
  makeStyles,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { STATUS_API } from 'src/app/constant/config';
import { MESSAGE } from 'src/app/constant/message';
import {
  applyHereEdits,
  approveAllHereEdits,
  approveHereEdits,
  generateHereEdits,
  getHereEdits,
  getHereEditStats,
  getHereSyncTargets,
  rejectAllHereEdits,
  rejectHereEdits,
  revertHereEdit
} from 'src/features/hereSlice';

const PAGE_SIZE = 20;

/** Số địa chỉ soi mỗi lần bấm "Dò đề xuất". Mỗi địa chỉ là một lượt hỏi sang server đích. */
const SCAN_LIMIT = 50;

const STATUS_LABEL = {
  pending: 'Chờ duyệt',
  approved: 'Đã duyệt',
  applied: 'Đã áp dụng',
  rejected: 'Từ chối',
  failed: 'Lỗi'
};

/**
 * Khối "Duyệt sửa địa chỉ".
 *
 * Đây là chỗ người vận hành quyết định: dịch vụ chỉ ĐỀ XUẤT, không tự ghi sang server bản đồ.
 * Chỉ những dòng đã duyệt mới đẩy đi được — ràng buộc đó nằm ở dịch vụ, giao diện chỉ phản ánh.
 *
 * Đề xuất luôn là "thêm số nhà", không bao giờ đụng đơn vị hành chính hay tên đường: đo trên 42
 * điểm thật cho thấy HERE hơn hẳn ở số nhà nhưng sai toàn bộ phần hành chính sau sáp nhập, còn
 * tên đường thì hai bên bất đồng mà chưa có cách phân định.
 */
const HereEditPanel = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const targets = useSelector(state => state.hereSlice.targets);
  const edits = useSelector(state => state.hereSlice.edits);
  const meta = useSelector(state => state.hereSlice.editsMeta);
  const stats = useSelector(state => state.hereSlice.editStats);
  const statusGetEdits = useSelector(state => state.hereSlice.statusGetEdits);
  const statusGenerate = useSelector(state => state.hereSlice.statusGenerateEdits);
  const statusDecide = useSelector(state => state.hereSlice.statusDecideEdits);
  const statusApply = useSelector(state => state.hereSlice.statusApplyEdits);

  const [targetId, setTargetId] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [street, setStreet] = useState('');
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState([]);
  const [confirmApply, setConfirmApply] = useState(false);
  const [confirmApproveAll, setConfirmApproveAll] = useState(false);

  const load = useCallback(
    (nextPage = 0) => {
      dispatch(getHereEdits({ targetId, status: statusFilter, street, page: nextPage + 1, limit: PAGE_SIZE }));
      dispatch(getHereEditStats({ targetId }));
    },
    [dispatch, targetId, statusFilter, street]
  );

  useEffect(() => {
    // Danh sách server dùng chung với khối bên trên; gọi lại ở đây để khối này đứng độc lập được.
    dispatch(getHereSyncTargets());
  }, [dispatch]);

  // Chọn sẵn server đầu tiên để người dùng không phải bấm thêm một bước mới thấy dữ liệu.
  useEffect(() => {
    if (!targetId && targets.length) setTargetId(targets[0].id);
  }, [targets, targetId]);

  useEffect(() => {
    setPage(0);
    setSelected([]);
    load(0);
  }, [load]);

  // Sau mỗi lần ghi, đọc lại: trạng thái của dòng vừa đổi nằm ở phía dịch vụ, không đoán ở đây.
  useEffect(() => {
    if (statusGenerate === STATUS_API.SUCCESS || statusDecide === STATUS_API.SUCCESS || statusApply === STATUS_API.SUCCESS) {
      setSelected([]);
      load(page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusGenerate, statusDecide, statusApply]);

  const changePage = (event, nextPage) => {
    setPage(nextPage);
    setSelected([]);
    load(nextPage);
  };

  // "Dò toàn bộ": generate soi theo lô SCAN_LIMIT, lặp tới khi hết địa chỉ chưa soi
  // (scanned === 0). Không gộp thành 1 call vì mỗi địa chỉ là một lượt gọi sang server
  // đích (tuần tự) — vài nghìn địa chỉ sẽ vượt timeout HTTP. Có thể bấm "Dừng" giữa chừng.
  const [scanAll, setScanAll] = useState(false);
  const [scanStat, setScanStat] = useState(null);
  const scanAllRef = useRef(false);

  const doScanAll = async () => {
    if (!targetId || scanAllRef.current) return;
    scanAllRef.current = true;
    setScanAll(true);
    let scanned = 0;
    let created = 0;
    try {
      while (scanAllRef.current) {
        const res = await dispatch(
          generateHereEdits({ targetId, limit: SCAN_LIMIT })
        ).unwrap();
        scanned += res?.scanned || 0;
        created += res?.created || 0;
        setScanStat({ scanned, created });
        if (!res?.scanned) break; // hết địa chỉ chưa soi
      }
    } catch (e) {
      // lỗi một lô -> dừng, giữ nguyên phần đã soi
    } finally {
      scanAllRef.current = false;
      setScanAll(false);
      load(page);
    }
  };

  // "Áp dụng": mỗi call apply tối đa PUSH_BATCH=200 dòng đã duyệt, đẩy lên MỌI server bản đồ đang
  // bật. Nếu >200 -> lặp tới khi applied === 0 (hết dòng approved). Có progress + Dừng giữa chừng.
  //
  // Vì sao vẫn lặp ở trình duyệt thay vì để dịch vụ đẩy một phát: mỗi lô là N lượt HTTP sang N
  // server bản đồ; gộp vài nghìn dòng vào một request sẽ vượt timeout và không có gì để hiện tiến độ.
  const [applying, setApplying] = useState(false);
  const [applyStat, setApplyStat] = useState({ applied: 0, total: 0 });
  const applyRef = useRef(false);

  const doApplyAll = async expectedTotal => {
    if (applyRef.current) return;
    applyRef.current = true;
    setApplying(true);
    // Chốt tổng trước khi chạy (mỗi lô apply làm approved giảm dần). Khi được gọi ngay sau một lần
    // duyệt thì `approvedCount` trong state còn là số CŨ, nên người gọi truyền tổng dự kiến vào.
    const total = expectedTotal ?? approvedCount;
    let applied = 0;
    setApplyStat({ applied: 0, total });
    try {
      while (applyRef.current) {
        const res = await dispatch(applyHereEdits()).unwrap();
        const n = res?.applied || 0;
        applied += n;
        setApplyStat({ applied, total });
        if (!n) break; // hết dòng đã duyệt
      }
    } catch (e) {
      // lỗi một lô -> dừng, phần đã áp dụng giữ nguyên
    } finally {
      applyRef.current = false;
      setApplying(false);
      load(page);
    }
  };

  /**
   * Duyệt xong là ĐẨY LUÔN sang mọi server bản đồ — không bắt bấm thêm "Áp dụng".
   *
   * Người vận hành đã quyết định ở bước duyệt; để dữ liệu nằm chờ ở trạng thái approved chỉ tạo ra
   * cảnh các bản đồ lệch nhau tuỳ theo ai nhớ bấm nốt bước hai. Nút "Áp dụng" giữ lại để chạy nốt
   * phần tồn (dòng duyệt từ trước, hoặc lô bị lỗi đang chờ đẩy lại).
   *
   * Duyệt hỏng thì KHÔNG đẩy: không có dòng nào chuyển sang approved để mà đẩy.
   */
  const approveThenApply = async action => {
    let changed = 0;
    try {
      const res = await dispatch(action).unwrap();
      changed = res?.changed || 0;
    } catch (e) {
      return;
    }
    if (changed) await doApplyAll(approvedCount + changed);
  };

  const toggle = id => setSelected(prev => (prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]));

  // Server bản đồ thật sự nhận dữ liệu khi duyệt: loại normalizer và đang bật. osm_service ngang
  // hàng không có lớp ghi đè nên không nằm trong nhóm này.
  const mapTargets = useMemo(() => targets.filter(item => (item.kind || 'normalizer') === 'normalizer' && item.enabled !== false), [targets]);

  // Chỉ chọn được dòng CHỜ DUYỆT — các trạng thái khác không đổi bằng nút duyệt/từ chối.
  const selectableIds = useMemo(() => edits.filter(item => item.status === 'pending').map(item => item.id), [edits]);
  const allSelected = selectableIds.length > 0 && selectableIds.every(id => selected.includes(id));

  const toggleAll = () => setSelected(allSelected ? [] : selectableIds);

  const busy = statusGenerate === STATUS_API.PENDING || statusDecide === STATUS_API.PENDING || statusApply === STATUS_API.PENDING;
  const approvedCount = stats.approved ?? 0;
  const pendingCount = stats.pending ?? 0;

  return (
    <Card className={classes.card}>
      <CardContent>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap">
          <Box>
            <Typography variant="h5">Duyệt sửa địa chỉ</Typography>
            <Typography variant="body2" color="textSecondary">
              So dữ liệu HERE với server bản đồ đích và bù phần thiếu. Chỉ thêm số nhà — đơn vị hành chính và tên đường
              giữ nguyên của server đích. Bấm <strong>Duyệt</strong> là ghi ngay lên{' '}
              <strong>{mapTargets.length}</strong> server bản đồ đang bật, không riêng server đang chọn.
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gridGap={8} mt={1}>
            {(scanAll || applying) && (
              <Box minWidth={180} mr={1}>
                <LinearProgress
                  variant={applying ? 'determinate' : 'indeterminate'}
                  value={
                    applying && applyStat.total
                      ? Math.min(100, Math.round((applyStat.applied / applyStat.total) * 100))
                      : undefined
                  }
                />
                <Typography variant="caption" color="textSecondary" noWrap component="div">
                  {applying
                    ? `Đang áp dụng: ${applyStat.applied}/${applyStat.total}`
                    : `Đang dò: ${scanStat?.scanned ?? 0} soi / ${scanStat?.created ?? 0} đề xuất`}
                </Typography>
              </Box>
            )}
            <Button
              variant="outlined"
              color="primary"
              disabled={busy || scanAll || !targetId}
              onClick={() => dispatch(generateHereEdits({ targetId, limit: SCAN_LIMIT }))}>
              Dò đề xuất
            </Button>
            {scanAll ? (
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => {
                  scanAllRef.current = false;
                }}>
                Dừng ({scanStat?.scanned ?? 0} soi / {scanStat?.created ?? 0} đề xuất)
              </Button>
            ) : (
              <Button
                variant="outlined"
                color="primary"
                disabled={busy || !targetId}
                onClick={doScanAll}>
                Dò toàn bộ
              </Button>
            )}
            {applying ? (
              <Button
                variant="contained"
                color="secondary"
                onClick={() => {
                  applyRef.current = false;
                }}>
                Dừng ({applyStat.applied}/{applyStat.total})
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                disabled={busy || scanAll || applying || !approvedCount}
                onClick={() => setConfirmApply(true)}>
                Áp dụng ({approvedCount})
              </Button>
            )}
          </Box>
        </Box>

        <Divider className={classes.divider} />

        <Box display="flex" flexWrap="wrap" gridGap={8}>
          {Object.keys(STATUS_LABEL).map(key => (
            <Chip
              key={key}
              size="small"
              variant={statusFilter === key ? 'default' : 'outlined'}
              color={statusFilter === key ? 'primary' : 'default'}
              label={`${STATUS_LABEL[key]}: ${stats[key] ?? 0}`}
              onClick={() => setStatusFilter(key)}
            />
          ))}
          <Chip
            size="small"
            variant={statusFilter === '' ? 'default' : 'outlined'}
            color={statusFilter === '' ? 'primary' : 'default'}
            label="Tất cả"
            onClick={() => setStatusFilter('')}
          />
        </Box>

        <Box display="flex" flexWrap="wrap" gridGap={8} alignItems="center" mt={2}>
          <FormControl variant="outlined" size="small" className={classes.select}>
            <InputLabel>Server bản đồ</InputLabel>
            <Select label="Server bản đồ" value={targetId} onChange={event => setTargetId(event.target.value)}>
              {targets.map(target => (
                <MenuItem key={target.id} value={target.id}>
                  {target.name}
                </MenuItem>
              ))}
            </Select>
            {/* Ô này chỉ quyết định DÒ đề xuất so với dữ liệu của server nào; ghi thì ghi lên tất cả. */}
            <FormHelperText>Dùng để dò &amp; lọc — khi duyệt vẫn ghi lên mọi server</FormHelperText>
          </FormControl>
          <TextField
            size="small"
            variant="outlined"
            label="Tên đường"
            value={street}
            onChange={event => setStreet(event.target.value)}
          />
          <Box flexGrow={1} />
          <Button
            size="small"
            variant="outlined"
            disabled={busy || !selected.length}
            onClick={() => approveThenApply(approveHereEdits(selected))}>
            Duyệt ({selected.length})
          </Button>
          <Button
            size="small"
            variant="outlined"
            className={classes.error}
            disabled={busy || !selected.length}
            onClick={() => dispatch(rejectHereEdits(selected))}>
            Từ chối
          </Button>
          {/* Ô tick chỉ chọn được TRANG đang xem (20 dòng). Với vài trăm đề xuất thì duyệt theo
              trang là hàng chục lần bấm, nên có nút riêng đi theo BỘ LỌC. */}
          <Button
            size="small"
            variant="contained"
            color="primary"
            disabled={busy || !pendingCount}
            onClick={() => setConfirmApproveAll(true)}>
            Duyệt tất cả ({pendingCount})
          </Button>
        </Box>

        <Table size="small" className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  color="primary"
                  checked={allSelected}
                  indeterminate={!allSelected && selected.length > 0}
                  disabled={!selectableIds.length}
                  onChange={toggleAll}
                />
              </TableCell>
              <TableCell>Server đích đang trả về</TableCell>
              <TableCell>Sau khi sửa</TableCell>
              <TableCell align="right">Tọa độ</TableCell>
              <TableCell align="right">Lệch</TableCell>
              <TableCell>Trạng thái</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {edits.map(item => (
              <TableRow key={item.id} hover>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    checked={selected.includes(item.id)}
                    disabled={item.status !== 'pending'}
                    onChange={() => toggle(item.id)}
                  />
                </TableCell>
                <TableCell className={classes.before}>{item.before_text || '—'}</TableCell>
                <TableCell>
                  <span className={classes.added}>{item.house_number}</span> {stripHouseNumber(item)}
                </TableCell>
                <TableCell align="right" className={classes.coords}>
                  {formatCoord(item.lat)}, {formatCoord(item.lon)}
                </TableCell>
                <TableCell align="right">{formatDistance(item.distance_m)}</TableCell>
                <TableCell>
                  {STATUS_LABEL[item.status] || item.status}
                  {item.decided_by && (
                    <Typography variant="caption" display="block" color="textSecondary">
                      {item.decided_by}
                    </Typography>
                  )}
                  {item.last_error && (
                    <Typography variant="caption" display="block" className={classes.error}>
                      {item.last_error}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  {item.status === 'applied' && (
                    <Button size="small" disabled={busy} onClick={() => dispatch(revertHereEdit(item.id))}>
                      Hoàn tác
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!edits.length && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  {statusGetEdits === STATUS_API.PENDING
                    ? 'Đang tải...'
                    : 'Chưa có đề xuất nào. Bấm "Dò đề xuất" để so dữ liệu đã thu với server đích.'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={meta?.total ?? 0}
          page={page}
          onChangePage={changePage}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} trên ${count}`}
        />
      </CardContent>

      <Dialog open={confirmApproveAll} onClose={() => setConfirmApproveAll(false)} fullWidth maxWidth="sm">
        <DialogTitle>{MESSAGE.CONFIRM_APPROVE_ALL}</DialogTitle>
        <DialogContent>
          <Typography>{MESSAGE.CONTENT_APPROVE_ALL}</Typography>
          <Box mt={2}>
            <Typography variant="body2">
              <strong>{pendingCount}</strong> đề xuất chờ duyệt
              {street ? ` · lọc theo đường "${street}"` : ''}
            </Typography>
            <Typography variant="body2">
              Sẽ ghi lên <strong>{mapTargets.length}</strong> server bản đồ: {mapTargets.map(item => item.name).join(', ') || '—'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmApproveAll(false)}>Hủy</Button>
          <Button
            className={classes.error}
            onClick={() => {
              dispatch(rejectAllHereEdits({ targetId, street: street || undefined }));
              setConfirmApproveAll(false);
            }}>
            Từ chối tất cả
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              setConfirmApproveAll(false);
              approveThenApply(approveAllHereEdits({ targetId, street: street || undefined }));
            }}>
            Duyệt tất cả
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmApply} onClose={() => setConfirmApply(false)} fullWidth maxWidth="sm">
        <DialogTitle>{MESSAGE.CONFIRM_APPLY_HERE_EDIT}</DialogTitle>
        <DialogContent>
          <Typography>{MESSAGE.CONTENT_APPLY_HERE_EDIT}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmApply(false)}>Hủy</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              setConfirmApply(false);
              doApplyAll();
            }}>
            Áp dụng
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

/**
 * Bỏ số nhà khỏi nhãn "sau khi sửa" để phần thêm vào được tô riêng ở cột bên phải.
 * Không cắt được thì trả nguyên nhãn — thà lặp số nhà còn hơn hiển thị sai.
 */
const stripHouseNumber = item => {
  const after = item.after_text || '';
  const prefix = `${item.house_number} `;
  return after.startsWith(prefix) ? after.slice(prefix.length) : after;
};

/**
 * Toạ độ của đề xuất — cũng chính là toạ độ ghi sang server bản đồ.
 *
 * Giữ 5 chữ số thập phân (~1 m) và không đổi dấu phẩy thập phân theo vùng: đây là con số để
 * người vận hành dán thẳng vào bản đồ hay lệnh tra cứu mà đối chiếu, không phải để đọc cho đẹp.
 */
const formatCoord = value => {
  const num = Number(value);
  return Number.isFinite(num) ? num.toFixed(5) : '—';
};

/** Khoảng cách giữa điểm HERE và điểm server đích nhận ra — càng xa càng đáng ngờ. */
const formatDistance = value => {
  const distance = Number(value);
  if (!Number.isFinite(distance)) return '—';
  return `${distance.toLocaleString('vi-VN', { maximumFractionDigits: 1 })} m`;
};

const useStyles = makeStyles(theme => ({
  card: { marginTop: theme.spacing(2) },
  divider: { margin: `${theme.spacing(2)}px 0` },
  table: { marginTop: theme.spacing(2) },
  select: { minWidth: 200 },
  // Chữ số đều bề ngang thì so hai dòng toạ độ với nhau mới nhanh được.
  coords: { fontFamily: 'monospace', whiteSpace: 'nowrap' },
  before: { color: theme.palette.text.secondary },
  added: {
    fontWeight: 600,
    color: theme.palette.success?.main || theme.palette.primary.main
  },
  error: { color: theme.palette.error.main }
}));

export default HereEditPanel;
